import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ShoppingCartIcon, UserIcon, MenuIcon, XIcon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
interface NavbarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}
export function Navbar({
  currentPage,
  onNavigate
}: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    totalItems
  } = useCart();
  const navLinks = [{
    name: 'About',
    id: 'about'
  }, {
    name: 'Menu',
    id: 'menu'
  }, {
    name: 'Catering',
    id: 'catering'
  }, {
    name: 'Contact',
    id: 'contact'
  }];
  const handleNavClick = (id: string) => {
    onNavigate(id);
    setIsMobileMenuOpen(false);
  };
  return <header className="sticky top-0 z-50 w-full bg-charcoal text-white shadow-md border-b border-amber/20" data-id="element-154">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-155">
        <div className="flex justify-between items-center h-20" data-id="element-156">
          {/* Left: Desktop Nav Links */}
          <nav className="hidden lg:flex space-x-8 flex-1" data-id="element-157">
            {navLinks.map(link => <button key={link.name} onClick={() => handleNavClick(link.id)} className={`font-heading text-lg transition-colors duration-200 uppercase tracking-wide ${currentPage === link.id ? 'text-amber' : 'text-white hover:text-amber'}`} data-id="element-158">
                {link.name}
              </button>)}
          </nav>

          {/* Center: Logo */}
          <div className="flex-shrink-0 flex items-center justify-start lg:justify-center flex-1 lg:flex-none cursor-pointer group" onClick={() => handleNavClick('home')} data-id="element-159">
            <motion.div whileHover={{
            scale: 1.05
          }} className="flex items-center" data-id="element-160">
              <img src="https://cdn.magicpatterns.com/uploads/64it8njJxRLMTBC7zyqg6G/VICTORY-LOGO-TRANSPARENT.png" alt="Victory Kitchen Z" className="h-20 w-auto" data-id="element-161" />
            </motion.div>
          </div>

          {/* Right: Actions */}
          <div className="hidden lg:flex items-center justify-end space-x-6 flex-1" data-id="element-162">
            <div className="flex items-center space-x-2 text-sm text-white/80" data-id="element-163">
              <MapPinIcon className="w-4 h-4 text-amber" data-id="element-164" />
              <span className="font-body font-medium hidden xl:inline" data-id="element-165">
                7323 Landover Rd
              </span>
            </div>
            <button onClick={() => handleNavClick('signin')} className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors" data-id="element-166">
              <UserIcon className="w-4 h-4" data-id="element-167" />
              <span className="font-heading tracking-wide" data-id="element-168">SIGN IN</span>
            </button>
            <button onClick={() => handleNavClick('cart')} className="relative p-2 hover:text-amber transition-colors" aria-label="Cart" data-id="element-169">
              <ShoppingCartIcon className="w-6 h-6" data-id="element-170" />
              {totalItems > 0 && <motion.span initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} key={totalItems} className="absolute top-0 right-0 bg-victory-red text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center transform translate-x-1 -translate-y-1" data-id="element-171">
                  {totalItems}
                </motion.span>}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center space-x-4" data-id="element-172">
            <button onClick={() => handleNavClick('cart')} className="relative p-2 hover:text-amber transition-colors" data-id="element-173">
              <ShoppingCartIcon className="w-6 h-6" data-id="element-174" />
              {totalItems > 0 && <motion.span initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} key={totalItems} className="absolute top-0 right-0 bg-victory-red text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center transform translate-x-1 -translate-y-1" data-id="element-175">
                  {totalItems}
                </motion.span>}
            </button>
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-white hover:text-amber transition-colors" data-id="element-176">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-177" /> : <MenuIcon className="w-6 h-6" data-id="element-178" />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-179">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-charcoal border-t border-white/10 overflow-hidden" data-id="element-180">
            <div className="px-4 pt-2 pb-6 space-y-4" data-id="element-181">
              <div className="flex items-center space-x-2 text-sm w-full py-3 border-b border-white/10 text-amber" data-id="element-182">
                <MapPinIcon className="w-5 h-5" data-id="element-183" />
                <span className="font-body font-medium text-white" data-id="element-184">
                  7323 Landover Rd, Hyattsville
                </span>
              </div>
              {navLinks.map(link => <button key={link.name} onClick={() => handleNavClick(link.id)} className={`block w-full text-left font-heading text-xl py-2 transition-colors uppercase tracking-wide ${currentPage === link.id ? 'text-amber' : 'text-white hover:text-amber'}`} data-id="element-185">
                  {link.name}
                </button>)}
              <button onClick={() => handleNavClick('signin')} className="flex items-center justify-center space-x-2 bg-amber hover:bg-amber/90 text-charcoal w-full py-3 rounded-md transition-colors mt-4" data-id="element-186">
                <UserIcon className="w-5 h-5" data-id="element-187" />
                <span className="font-heading tracking-wide text-lg" data-id="element-188">
                  SIGN IN
                </span>
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}